/**
 * @deprecated - new confrim box using swal @link https://sweetalert2.github.io/
 *
 * @file confirm-changes.tsx
 * @module app/modals/confirm-changes
 * @description Renders the confirm changes modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import Modal from 'react-bootstrap/Modal';
import CrossSvg from '../../_admin/assets/media/close.png';
import { Button, Col, Row } from 'react-bootstrap';
import { useState, useRef, useEffect } from 'react';

const ConfirmChanges = (props: any) => {
  const [photoCaptured, setPhotoCaptured] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleCapture = async (mode?: 'user' | 'environment') => {
    const targetMode = mode || facingMode;
    
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }

    setCapturedPhoto(null);
    setPhotoCaptured(false);
    setIsCameraLoading(true);
    setIsVideoReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: targetMode }
      });
      setCameraStream(stream);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    } finally {
      setIsCameraLoading(false);
    }
  };

  const handleSwitchCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    handleCapture(newMode);
  };

  const handlePhotoCapture = () => {
    if (videoRef.current && videoRef.current.videoWidth > 0) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const context = canvas.getContext('2d');

      if (context) {
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const photo = canvas.toDataURL('image/png');
        setCapturedPhoto(photo);
        setPhotoCaptured(true);

        if (cameraStream) {
          cameraStream.getTracks().forEach((track) => track.stop());
          setCameraStream(null);
        }
      }
    }
  };

  const handleModalClose = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setPhotoCaptured(false);
    setCapturedPhoto(null);
    props.onHide();
  };

  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.muted = true;
      videoRef.current.play().catch((err) => {
        console.error('Error playing video:', err);
      });
    }
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  return (
    <>
      <Modal
        show={props.show}
        onHide={handleModalClose}
        dialogClassName="modal-dialog-centered min-w-lg-590px"
        className="border-r10px"
      >
        <Modal.Header className="border-bottom-0 pb-0 text-center mx-auto">
          <img
            className="close-inner-top cursor-pointer"
            width={40}
            height={40}
            src={CrossSvg}
            alt="closebutton"
            onClick={handleModalClose}
          />
          <Modal.Title className="fs-26 fw-bolder mw-lg-375px pt-5">
            {props.title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-5">
          {props.isRemove && (
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="bg-light-danger text-center border-r10px p-3">
                  <span className="fs-18 fw-500">{props.removeMsg}</span>
                </div>
              </div>
            </div>
          )}

          {props.showPhotoCapture && (
            <div className={props.isRemove ? "mt-5" : ""}>
              {cameraStream && (
                <div className="text-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    onLoadedData={() => setIsVideoReady(true)}
                    className="border"
                    style={{
                      width: '100%',
                      height: '300px',
                      backgroundColor: '#000',
                      display: 'block',
                      objectFit: 'cover'
                    }}
                  />
                  <div className="d-flex justify-content-center gap-3">
                    <Button variant="success" className="uw-btn uw-btn-success mt-3" onClick={handlePhotoCapture} disabled={!isVideoReady || isCameraLoading}>
                      Capture Photo
                    </Button>
                    <Button variant="primary" className="uw-btn uw-btn-primary mt-3" onClick={handleSwitchCamera} disabled={isCameraLoading}>
                      {isCameraLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Switching...
                        </>
                      ) : (
                        "Switch Camera"
                      )}
                    </Button>
                  </div>
                </div>
              )}
              {capturedPhoto && !cameraStream && (
                <div className="text-center">
                  <img
                    src={capturedPhoto}
                    alt="Captured"
                    className="border"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      display: 'block'
                    }}
                  />
                </div>
              )}
              {isCameraLoading && !cameraStream && (
                <div className="text-center" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <span className="spinner-border text-primary mb-3"></span>
                  <p className="fs-5 text-muted">Opening camera, please wait...</p>
                </div>
              )}
              {!cameraStream && !capturedPhoto && !isCameraLoading && (
                <div className="text-center" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <p className="text-muted">Click "Capture" to open your camera</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="pt-0 pb-8 mb-4 border-top-0">
          <Row className="align-items-center justify-content-center g-3 w-100">
            {props.showPhotoCapture && (
              <Col xs="auto">
                <Button className="uw-btn uw-btn-light uw-btn-lg"
                  variant="secondary"
                  size="lg"
                  onClick={() => handleCapture()}
                  disabled={!!cameraStream || props?.loading || isCameraLoading}
                >
                  {photoCaptured ? 'Recapture' : 'Capture'}
                </Button>
              </Col>
            )}
            <Col xs="auto">
              <Button className="uw-btn uw-btn-primary uw-btn-lg"
                variant="primary"
                size="lg"
                onClick={() => props.onSave(capturedPhoto)}
                disabled={props?.loading || (props.showPhotoCapture && !photoCaptured)}
              >
                {!props.loading && (
                  <span className="indicator-label fs-16 fw-bold">
                    {props.save ? props.save : 'Save'}
                  </span>
                )}
                {props.loading && (
                  <span
                    className="indicator-progress fs-16 fw-bold"
                    style={{ display: 'block' }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </Col>
            <Col xs="auto">
              <Button
                variant="danger"
                size="lg"
                className="uw-btn uw-btn-danger uw-btn-lg btn-lg"
                onClick={handleModalClose}
              >
                Discard
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ConfirmChanges;
